import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { STATUS_OPTIONS, TASK_TYPES, type GanttBundle } from '@pathonline/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { sendJson, useRun, useStoredState, useView, type Note } from '../api.ts';
import { plural } from '../format.ts';
import { GanttChart } from '../gantt/GanttChart.tsx';
import { buildRows, DEFAULT_FILTERS, ROLE_ORDER, typeLabel, type Filters } from '../gantt/model.ts';
import { NodePanel } from '../gantt/NodePanel.tsx';
import { ThreadModal } from '../gantt/ThreadModal.tsx';
import { SideToggle } from '../SideToggle.tsx';
import '../gantt/gantt.css';

const ganttRoute = getRouteApi('/gantt');
const FILTER_KEY = 'gantt.filters';

const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
/** For a list where empty means all: is `v` shown, and the list after clicking `v`. */
const ticked = (list: string[], v: string) => !list.length || list.includes(v);
const toggleAll = (all: readonly string[], list: string[], v: string) => {
  const next = toggle(list.length ? list : [...all], v);
  return next.length === all.length ? [] : next;
};
/** The old sidebar's order: the five common ones first. */
const FIRST = ['ACTIVE', 'SEEKING', 'IDEA', 'PAUSED', 'DONE'];
const STATUS_ORDER = [...FIRST, ...STATUS_OPTIONS.filter((s) => !FIRST.includes(s))];
/** The old sidebar's names for the task categories. */
const SHOW_LABEL: Record<string, string> = { Topic: 'Topics', Task: 'Tasks', Initiative: 'Initiatives' };
/** The task whose thread may open, as the comment button: for someone who may comment, or when there is something to read. */
const threadNodeOf = (b: GanttBundle, id: string) =>
  b.canComment || (b.commentCounts[id] ?? 0) > 0 ? b.nodes.find((n) => n.id === id) : undefined;

export function GanttPage() {
  const { node, thread: threadLink } = ganttRoute.useSearch();
  const navigate = useNavigate({ from: '/gantt' });
  const gantt = useView<GanttBundle>('gantt', 60_000);
  const [filters, store] = useStoredState<Filters>(FILTER_KEY, DEFAULT_FILTERS);
  // The filters as last set. Code that runs after a save reads this: its own `filters` may be old.
  const latest = useRef(filters);
  // Phones: the filters fold away under a "☰ Filters" bar, as old.
  const [sideOpen, setSideOpen] = useState(false);
  // "Focus & expand" is an exploration gesture, not a filter: not remembered.
  const [focus, setFocus] = useState<string | undefined>();
  // The mail's "respond" link (?node=…&thread=1) lands in the thread, over the task's panel, reply box focused,
  // as the old ?node= did (DP:7684, 7209); so does an old /?dest=?view=gantt&node= link (dest.ts). A plain ?node= opens the panel only.
  const [thread, setThread] = useState<string | null>(() => (node && threadLink ? node : null));
  // `after` runs once the views are refetched, with what `f` answered.
  const { run, busy, msg: flash } = useRun();
  const qc = useQueryClient();

  const update = (f: Filters) => {
    latest.current = f;
    store(f);
  };
  const select = (id?: string) => navigate({ search: id ? { node: id } : {} });

  // The same link while the page is open; then the flag leaves the URL, so a reload shows the panel only.
  useEffect(() => {
    if (!node || !threadLink) return;
    setThread(node);
    void navigate({ search: { node }, replace: true });
  }, [node, threadLink, navigate]);

  const rows = useMemo(() => (gantt.data ? buildRows(gantt.data, filters, focus) : []), [gantt.data, filters, focus]);
  // A link's thread this bundle cannot show is dropped, once the bundle is current (not while a newer one
  // is on its way). Kept, it would open by itself after a later refetch, with nothing asking for it.
  if (gantt.data && !gantt.isFetching && thread && !threadNodeOf(gantt.data, thread)) setThread(null);

  if (gantt.isPending) return <p className="muted pad">Loading the Gantt…</p>;
  // A reload that fails keeps the page, and what is typed in it: the error is said in the corner.
  if (gantt.isLoadingError) return <p className="error pad">Could not load the Gantt: {gantt.error.message}</p>;
  const b = gantt.data;
  const allStatuses = filters.statuses.length === 0;
  const threadNode = thread ? threadNodeOf(b, thread) : undefined;
  const tasks = rows.filter((r) => r.kind === 'task').length;
  const mine = b.scoped || (!!b.me.person && filters.scope === 'my');

  const newTask = () => sendJson<{ id: string }>('POST', '/api/nodes', { label: 'New task', nodeType: 'Task', projectId: filters.programme });
  // Opened after the refetch: before it, the bundle has no such task and the panel says "not on your Gantt".
  // A refetch that failed brought no new task: nothing opens then (the corner says the reload failed).
  // Built from `latest`: a filter changed while the save ran is kept.
  const openNew = (answer: unknown) => {
    const id = (answer as { id: string }).id;
    if (!qc.getQueryData<GanttBundle>(['views', 'gantt'])?.nodes.some((n) => n.id === id)) return;
    const cur = latest.current;
    if (!cur.includeUndated) update({ ...cur, includeUndated: true });
    select(id);
  };

  return (
    <div className={`gantt-page ${sideOpen ? 'side-open' : ''}`}>
      <SideToggle open={sideOpen} onToggle={() => setSideOpen(!sideOpen)} />
      <aside className="side g-side" aria-label="Gantt filters">
        <div className="seg" role="group" aria-label="Whose tasks">
          <button
            type="button"
            aria-pressed={mine}
            disabled={!b.scoped && !b.me.person}
            title={!b.scoped && !b.me.person ? 'Your account is not linked to a person' : undefined}
            onClick={() => update({ ...filters, scope: 'my' })}
          >
            My
          </button>
          <button
            type="button"
            aria-pressed={!b.scoped && (!b.me.person || filters.scope === 'all')}
            disabled={b.scoped}
            title={b.scoped ? 'Your role shows only your own tasks' : undefined}
            onClick={() => update({ ...filters, scope: 'all' })}
          >
            All
          </button>
        </div>
        {b.scoped ? (
          <p className="g-note">Your role shows only the tasks you are on, and their grants.</p>
        ) : !b.me.person ? (
          <p className="g-note">Your account has no person name, so “My” is unavailable.</p>
        ) : (
          mine && <p className="g-note">Tasks involving {b.me.person}.</p>
        )}
        {focus && (
          <p className="g-note">
            Focused on one grant — every task in it, whoever is on it.{' '}
            <button type="button" className="link" onClick={() => setFocus(undefined)}>Exit focus</button>
          </p>
        )}
        {b.canEdit && (
          <button type="button" className="btn btn-sm g-new" disabled={busy} onClick={() => run(newTask, 'Task created — fill in the details', openNew)}>
            + New task
          </button>
        )}

        <div className="side-h">Show</div>
        <div role="group" aria-label="Show">
          {TASK_TYPES.map((t) => (
            <label key={t}>
              <input type="checkbox" checked={ticked(filters.types, t)} onChange={() => update({ ...filters, types: toggleAll(TASK_TYPES, filters.types, t) })} />
              {SHOW_LABEL[t] ?? typeLabel(t)}
            </label>
          ))}
          <label>
            <input type="checkbox" checked={filters.includeUndated} onChange={() => update({ ...filters, includeUndated: !filters.includeUndated })} />
            Include undated
          </label>
        </div>

        <hr className="side-sep" />
        <div className="side-h">Status</div>
        <div role="group" aria-label="Status">
          <label className="g-all">
            <input
              type="checkbox"
              checked={allStatuses}
              ref={(el) => {
                if (el) el.indeterminate = !allStatuses && filters.statuses.length > 0;
              }}
              onChange={() => update({ ...filters, statuses: allStatuses ? ['ACTIVE'] : [] })}
            />
            <b>All statuses</b>
          </label>
          {STATUS_ORDER.map((s) => (
            <label key={s}>
              <input type="checkbox" checked={ticked(filters.statuses, s)} onChange={() => update({ ...filters, statuses: toggleAll(STATUS_OPTIONS, filters.statuses, s) })} />
              {s}
            </label>
          ))}
        </div>

        <hr className="side-sep" />
        <div className="side-h">Programme</div>
        <div className="pills" role="group" aria-label="Programme">
          <button type="button" aria-pressed={!filters.programme} onClick={() => update({ ...filters, programme: '' })}>All</button>
          {[...b.projects].sort((x, y) => x.order - y.order).map((p) => (
            <button key={p.id} type="button" title={p.name} aria-pressed={filters.programme === p.id} onClick={() => update({ ...filters, programme: p.id })}>
              {p.id}
            </button>
          ))}
        </div>

        <hr className="side-sep" />
        <div className="side-h">Roles shown</div>
        <div role="group" aria-label="Roles shown on the bars">
          {ROLE_ORDER.map((r) => (
            <label key={r}>
              <input type="checkbox" checked={filters.roles.includes(r)} onChange={() => update({ ...filters, roles: toggle(filters.roles, r) })} />
              <span className={`rf-dot r-${r}`} />
              {r}
            </label>
          ))}
        </div>

        <hr className="side-sep" />
        <div className="side-h">People</div>
        <label>
          Min focus
          <select value={filters.minFocus} onChange={(e) => update({ ...filters, minFocus: Number(e.target.value) })}>
            {[0, 1, 2, 3, 4, 5].map((f) => <option key={f} value={f}>{f}+</option>)}
          </select>
        </label>
        {!b.scoped && filters.scope === 'all' && (
          <label>
            Involves
            <select value={filters.person} onChange={(e) => update({ ...filters, person: e.target.value })}>
              <option value="">anyone</option>
              {filters.person && !b.people.some((p) => p.name === filters.person) && <option>{filters.person}</option>}
              {b.people.map((p) => p.name).sort().map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
        )}
        <button type="button" className="link g-reset" onClick={() => update({ ...DEFAULT_FILTERS, scope: filters.scope })}>Reset filters</button>
      </aside>

      <div className={`gantt-body ${node ? 'with-panel' : ''}`}>
        <div className="g-main">
          <GanttChart
            bundle={b}
            rows={rows}
            filters={filters}
            selected={node}
            onSelect={select}
            focus={focus}
            onFocus={setFocus}
            onThread={setThread}
            onReparent={(child, parent) =>
              run(
                () => sendJson('PATCH', `/api/nodes/${encodeURIComponent(child)}`, { field: 'ParentID', value: parent }),
                `“${b.nodes.find((n) => n.id === child)?.label}” is now under “${b.nodes.find((n) => n.id === parent)?.label}”`,
              )
            }
            corner={
              <>
                <p className="g-status" role="status">
                  {plural(tasks, 'task')}
                </p>
                <SaveNote flash={flash} />
                {gantt.isRefetchError && <p className="g-msg error">Could not reload the Gantt: {gantt.error.message}</p>}
              </>
            }
          />
        </div>
        {node && <NodePanel key={node} bundle={b} nodeId={node} onClose={() => select(undefined)} onGone={() => select(undefined)} />}
      </div>
      {threadNode && <ThreadModal b={b} node={threadNode} onClose={() => setThread(null)} />}
    </div>
  );
}

/** The save message. A success clears itself after a few seconds; an error stays until the next save. */
function SaveNote({ flash }: { flash: Note | null }) {
  const [gone, setGone] = useState<Note | null>(null);
  useEffect(() => {
    if (!flash || flash.bad) return;
    const t = setTimeout(() => setGone(flash), 5000);
    return () => clearTimeout(t);
  }, [flash]);
  const note = flash === gone ? null : flash;
  return (
    <p className={`g-msg ${note?.bad ? 'error' : 'ok'}`} role="status" title={note?.text}>
      {note?.text}
    </p>
  );
}
